// Build the per-project document archive from everything collected so far:
// files the user uploaded, the building-file (תיק בניין) documents, and the
// applicable plans. Reads a loaded project ({ project, documents, ... }) where
// project.identification is the saved §8 snapshot.
export function buildArchive(loaded) {
  const idn = loaded?.project?.identification || {};
  const sources = idn.sources || [];
  const buildingFile = idn.buildingFile || {};
  const plans = idn.plans || [];
  const projectDocs = loaded?.documents || [];
  // When the agent last collected/identified this property — used as the
  // "read by agent" timestamp for the sources it pulled (spec 2.3).
  const collectedAt = loaded?.project?.identification_at || null;

  // 1. Uploaded by us — New-Project documents + manual gap completions. These are
  // user files the agent hasn't ingested into the analysis yet (agentReadAt=null).
  const uploads = [
    ...projectDocs.map((d) => ({
      id: `pd-${d.id}`,
      name: d.file_name || d.document_type || 'מסמך',
      meta: d.document_type || '',
      date: d.created_at || null,
      agentReadAt: null,
      link: d.file_id && /^https?:\/\//.test(d.file_id) ? d.file_id : null, // stored blob URL
    })),
    ...sources.filter((s) => s.manual).map((s) => ({
      id: s.id,
      name: s.fileName || s.source,
      meta: s.source !== s.fileName ? s.source : 'מסמך משתמש',
      date: s.date || null,
      agentReadAt: null,
      link: s.link || null,
    })),
    // Documents attached to an inference route in the existing-condition stage
    // (chunk 04 §11: "a document uploaded from inside a route also enters the
    // project's document archive"). They used to live only in that stage's
    // snapshot and never appeared here.
    ...((loaded?.project?.existing?.routes) || []).flatMap((r) =>
      (r.documents || []).map((d, i) => ({
        id: `ex-${r.id}-${i}`,
        name: d.name || 'מסמך',
        meta: `מסלול הסקה · ${r.name || ''}`.trim(),
        date: d.at || null,
        agentReadAt: null,
        link: d.link || null,
      }))),
    // Docs uploaded to complete gaps in the statutory-analysis stage (chunk 03 §12).
    ...((loaded?.project?.statutory?.groups) || []).flatMap((g) =>
      (g.fields || [])
        .filter((f) => f.source && f.source.uploaded && f.source.link)
        .map((f) => ({
          id: `st-${f.id}`,
          name: f.source.label || f.value || 'מסמך',
          meta: `השלמת נתון · ${g.title}`,
          date: f.source.at || null,
          agentReadAt: null,
          link: f.source.link,
        }))),
  ];

  // 2. תיק בניין — flatten the scraped categories into individual documents. The
  // agent read them when it scraped the file (buildingFile.fetchedAt).
  const buildingDocs = (buildingFile.categories || []).flatMap((c) =>
    (c.docs || []).map((d, i) => ({
      id: `bf-${c.key}-${i}`,
      name: d.type,
      meta: c.label,
      category: c.label,
      date: d.date || null,
      agentReadAt: buildingFile.fetchedAt || collectedAt,
      link: d.link || null, // handasa DocViewer link — opens the document directly
    })),
  );

  // 3. תכניות — each applicable plan with its official document link. Collected
  // by the agent during identification.
  const planDocs = plans.map((p) => ({
    id: `pl-${p.id}`,
    name: p.name,
    ref: p.number,
    meta: p.type && p.type !== '—' ? p.type : 'תכנית',
    date: p.date || null,
    agentReadAt: collectedAt,
    link: p.planUrl || null,
  }));

  const handasaLink = idn.identification?.gush && idn.identification?.helka
    ? `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${idn.identification.gush}_${idn.identification.helka}`
    : null;

  return {
    groups: [
      { key: 'uploads', label: 'שהועלו על ידכם', icon: 'fi-rr-cloud-upload', docs: uploads },
      { key: 'building', label: 'תיק בניין', icon: 'fi-rr-folder', docs: buildingDocs, link: handasaLink, linkLabel: 'פתח תיק בניין' },
      { key: 'plans', label: 'תכניות חלות', icon: 'fi-rr-document', docs: planDocs },
    ],
    total: uploads.length + buildingDocs.length + planDocs.length,
  };
}
